import { useMemo } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';

import { Badge } from '@/core/components/Badge';

import { mapSourceCredibilityToReportScore } from '@/features/Reports/helpers';
import { buildMediaProxyUrl } from '@/features/Reports/media-proxy';
import { ReportScore } from '@/features/Reports/components/Source/Source.types';

import { getTimeAndDate } from '@/utils/helpers';

import {
  Body,
  Footer,
  MediaCardButton,
  Thumbnail,
  ThumbnailImage,
  TimeAgo,
  Title,
} from './MediaCard.styles';
import { MediaCardProps } from './MediaCard.types';

const getReliabilityColor = (
  score: ReportScore,
  reliability: DefaultTheme['colors']['reliability']
) => {
  switch (score) {
    case ReportScore.HIGH:
      return reliability.high;
    case ReportScore.MEDIUM:
      return reliability.medium;
    case ReportScore.LOW:
      return reliability.low;
    default:
      return reliability.unknown;
  }
};

export const MediaCard = ({
  resolvedMedia,
  title,
  name,
  credibility,
  publishedDatetime,
  onClick,
}: MediaCardProps) => {
  const { colors } = useTheme();

  const badgeColor = useMemo(
    () => getReliabilityColor(mapSourceCredibilityToReportScore(credibility), colors.reliability),
    [credibility, colors.reliability]
  );

  const timeAgo = publishedDatetime
    ? getTimeAndDate({
        dateStr: publishedDatetime,
        fromNow: true,
        fromNowSuffix: true,
      })
    : null;

  const thumbnailSrc = buildMediaProxyUrl(resolvedMedia.thumbnailUrl);

  return (
    <MediaCardButton
      type="button"
      onClick={onClick}
    >
      <Thumbnail>
        {thumbnailSrc && (
          <ThumbnailImage
            src={thumbnailSrc}
            alt={title}
          />
        )}
      </Thumbnail>
      <Body>
        <Title>{title}</Title>
        <Footer>
          <Badge
            text={name}
            backgroundColor={badgeColor}
            color={colors.badgeText.dark}
            isNormalcase
          />
          {timeAgo && <TimeAgo color={colors.secondaryText}>{timeAgo}</TimeAgo>}
        </Footer>
      </Body>
    </MediaCardButton>
  );
};
